import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth, db,
  onAuthStateChanged, signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider, signInWithPopup,
  getOrCreateUser, getUserProStatus,
  FIREBASE_ENABLED,
} from './firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,      setUser]      = useState(null);   // Firebase user object
  const [profile,   setProfile]   = useState(null);   // Firestore user data
  const [isPro,     setIsPro]     = useState(false);  // Pro status
  const [loading,   setLoading]   = useState(true);   // Auth state loading
  const [authReady, setAuthReady] = useState(false);

  // ── Listen for auth state changes ────────────────────────────────────────
  useEffect(() => {
    if (!FIREBASE_ENABLED || !auth) {
      const localPro = localStorage.getItem('adm_pro') === 'true';
      setIsPro(localPro);
      setLoading(false);
      setAuthReady(true);
      return;
    }

    // Safety timeout — if Firebase takes more than 5s, unblock the UI
    const timeout = setTimeout(() => {
      setLoading(false);
      setAuthReady(true);
      setIsPro(localStorage.getItem('adm_pro') === 'true');
    }, 5000);

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(timeout);
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userData = await getOrCreateUser(firebaseUser);
          setProfile(userData);
          const proStatus = await getUserProStatus(firebaseUser.uid);
          setIsPro(proStatus);
          localStorage.setItem('adm_pro', proStatus ? 'true' : 'false');
        } catch(err) {
          console.error('Profile load error:', err);
          setIsPro(localStorage.getItem('adm_pro') === 'true');
        }
      } else {
        setUser(null);
        setProfile(null);
        setIsPro(false);
        localStorage.removeItem('adm_pro');
      }
      setLoading(false);
      setAuthReady(true);
    });

    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  // ── Refresh pro status — retries 3 times with 1s delay ──────────────────
  const refreshProStatus = async () => {
    if (!user) return;
    // Retry up to 3 times — Firestore write may not be instant
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1200 * attempt));
        const proStatus = await getUserProStatus(user.uid);
        if (proStatus) {
          setIsPro(true);
          localStorage.setItem('adm_pro', 'true');
          return;
        }
      } catch(_) {}
    }
    // Final check from localStorage fallback
    if (localStorage.getItem('adm_pro') === 'true') {
      setIsPro(true);
    }
  };

  // ── Sign in with email/password ──────────────────────────────────────────
  const signIn = async (email, password) => {
    if (!auth) throw new Error('Firebase not configured');
    return signInWithEmailAndPassword(auth, email, password);
  };

  // ── Sign up with email/password ──────────────────────────────────────────
  const signUp = async (email, password) => {
    if (!auth) throw new Error('Firebase not configured');
    return createUserWithEmailAndPassword(auth, email, password);
  };

  // ── Sign in with Google ──────────────────────────────────────────────────
  const signInWithGoogle = async () => {
    if (!auth) throw new Error('Firebase not configured');
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  // ── Sign out ─────────────────────────────────────────────────────────────
  const logOut = async () => {
    if (auth) await signOut(auth);
    setUser(null); setProfile(null); setIsPro(false);
    localStorage.removeItem('adm_pro');
  };

  // ── Reset password ───────────────────────────────────────────────────────
  const resetPassword = async (email) => {
    if (!auth) throw new Error('Firebase not configured');
    return sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider value={{
      user, profile, isPro, loading, authReady,
      signIn, signUp, signInWithGoogle, logOut, resetPassword,
      refreshProStatus,
      firebaseEnabled: FIREBASE_ENABLED,
      db,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
